import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, TextField, Button, Chip, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import ArrowForwardIcon          from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon    from '@mui/icons-material/CheckCircleOutline';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import FolderOpenOutlinedIcon    from '@mui/icons-material/FolderOpenOutlined';
import VerifiedOutlinedIcon      from '@mui/icons-material/VerifiedOutlined';
import { keyframes } from '@emotion/react';
import { useForm, ValidationError } from '@formspree/react';
import ClientImage from '../assets/client.png';
import { C, F, G, SH } from '../styles/tokens';
import { fieldSx, btnBlueSx } from '../styles/mixins';

const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh',
  'Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka',
  'Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram',
  'Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana',
  'Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
  'Andaman & Nicobar Islands','Chandigarh',
  'Dadra & Nagar Haveli and Daman & Diu','Delhi (NCT)',
  'Jammu & Kashmir','Ladakh','Lakshadweep','Puducherry',
];

const REGISTRATION_TYPES = [
  'Private Limited Company',
  'Public Limited Company',
  'One Person Company (OPC)',
  'Limited Liability Partnership (LLP)',
  'Partnership Firm',
  'Sole Proprietorship',
  'Section 8 Company (NGO / Non-Profit)',
  'Producer Company',
  'Nidhi Company',
  'GST Registration',
  'Other Registrations',
];

const selectSx = {
  borderRadius: '10px',
  backgroundColor: C.bg,
  fontFamily: F.body,
  fontSize: 14,
  '& fieldset':             { borderColor: C.border },
  '&:hover fieldset':       { borderColor: C.blue },
  '&.Mui-focused fieldset': { borderColor: C.blue, borderWidth: 1.5 },
};

const labelSx = {
  fontFamily: F.body,
  fontSize: 14,
  '&.Mui-focused': { color: C.blue },
};

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-12px); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const TRUST_POINTS = [
  'Quick & Hassle-Free Registration',
  'Expert CA & CS Guidance',
  'End-to-End Compliance Support',
];

const MOBILE_NUMBER_RE = /^[6-9][0-9]{9}$/;

// ── CallMeBot WhatsApp notification ───────────────────────────────────────────
// Replace CALLMEBOT_API_KEY with the key received after activating CallMeBot.
// Activation: save +34 644 37 79 78, send "I allow callmebot to send me messages" on WhatsApp.
const CALLMEBOT_API_KEY = 'REPLACE_WITH_YOUR_KEY';
const WHATSAPP_NOTIFY_NUMBER = '919311817126'; // country code + number, no +

const sendWhatsAppNotification = (data) => {
  const msg =
    `📋 New Pracco Inquiry\n` +
    `👤 Name: ${data.name}\n` +
    `📧 Email: ${data.email}\n` +
    `📱 Mobile: ${data.mobile}\n` +
    `🏛 State: ${data.state}\n` +
    `📝 Type: ${data.registrationType}`;
  const url = `https://api.callmebot.com/whatsapp.php?phone=${WHATSAPP_NOTIFY_NUMBER}&text=${encodeURIComponent(msg)}&apikey=${CALLMEBOT_API_KEY}`;
  fetch(url).catch(() => {}); // best-effort, silent fail
};

const FormSection = () => {
  const [state, handleSubmit] = useForm('mnnpwlwq');
  const [formStatus, setFormStatus] = useState(null);
  const [fields, setFields] = useState({ name: '', email: '', mobile: '', state: '', registrationType: '' });
  const pendingData = React.useRef(null);

  const handleChange = (e) => setFields((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  useEffect(() => {
    if (state.succeeded) {
      if (pendingData.current) {
        sendWhatsAppNotification(pendingData.current);
        pendingData.current = null;
      }
      setFormStatus('success');
      setFields({ name: '', email: '', mobile: '', state: '', registrationType: '' });
      const t = setTimeout(() => setFormStatus(null), 4000);
      return () => clearTimeout(t);
    }
    if (state.errors?.length) setFormStatus('error');
  }, [state.succeeded, state.errors]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!MOBILE_NUMBER_RE.test(fields.mobile)) {
      alert('Please enter a valid 10-digit mobile number starting with 6–9');
      return;
    }
    if (!fields.state) {
      alert('Please select your State of Registration');
      return;
    }
    if (!fields.registrationType) {
      alert('Please select the Type of Registration');
      return;
    }
    pendingData.current = { ...fields }; // snapshot before Formspree clears state
    handleSubmit(e);
  };

  const mobileError = fields.mobile && !MOBILE_NUMBER_RE.test(fields.mobile);

  return (
    <Box
      sx={{
        background: G.hero,
        pt: { xs: '88px', md: '96px' },
        pb: { xs: 6, md: 8 },
        px: { xs: 2, md: 6 },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display:   { xs: 'block', md: 'flex' },
          alignItems: 'center',
          gap:        { xs: 5, md: 6 },
          maxWidth:   1200,
          mx:         'auto',
          animation:  `${fadeUp} 0.7s ease-out both`,
        }}
      >
        {/* ── Left: hero copy ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Chip
            label="INDIA'S TRUSTED CORPORATE SERVICES"
            size="small"
            sx={{
              backgroundColor: C.amberLight,
              color:           '#92400E',
              fontWeight:      700,
              fontFamily:      F.heading,
              fontSize:        10.5,
              letterSpacing:   0.8,
              border:          `1px solid ${C.amberBorder}`,
              mb:              2.5,
            }}
          />

          <Typography
            component="h1"
            sx={{
              fontFamily: F.heading,
              fontSize:   { xs: 32, sm: 40, md: 46 },
              fontWeight: 800,
              lineHeight: 1.15,
              color:      C.navy,
              mb:         2,
            }}
          >
            Register Your Business.
            <br />
            <Box component="span" sx={{ color: C.blue }}>Stay Compliant.</Box>
          </Typography>

          <Typography
            sx={{
              fontFamily: F.body,
              fontSize:   { xs: 15, md: 16 },
              color:      C.textMid,
              lineHeight: 1.75,
              mb:         3,
              maxWidth:   480,
            }}
          >
            Kickstart your business with hassle-free company registration and stay
            on top of GST, tax filing, and accounting — all under one roof.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 4 }}>
            {TRUST_POINTS.map((point) => (
              <Box key={point} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleOutlineIcon sx={{ color: C.blue, fontSize: 18 }} />
                <Typography sx={{ fontFamily: F.body, fontSize: 14, color: C.text, fontWeight: 500 }}>
                  {point}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            component="img"
            src={ClientImage}
            alt="Business professionals"
            sx={{
              width:   '100%',
              maxWidth: 420,
              display: { xs: 'none', md: 'block' },
              animation: `${float} 4s ease-in-out infinite`,
            }}
          />
        </Box>

        {/* ── Right: form card ── */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <Paper
            elevation={0}
            sx={{
              width:        '100%',
              maxWidth:     440,
              p:            { xs: 3, md: 4 },
              borderRadius: '20px',
              background:   C.white,
              border:       `1px solid ${C.border}`,
              boxShadow:    SH.form,
            }}
          >
            <Typography sx={{ fontFamily: F.heading, fontSize: 20, fontWeight: 700, color: C.navy, mb: 0.5 }}>
              Get Free Consultation
            </Typography>
            <Typography sx={{ fontFamily: F.body, fontSize: 13, color: C.textMuted, mb: 2.5 }}>
              Our experts will reach out within 24 hours.
            </Typography>

            <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField label="Your Name" name="name" fullWidth required value={fields.name} onChange={handleChange} sx={fieldSx} />
              <TextField label="Email Address" name="email" type="email" fullWidth required value={fields.email} onChange={handleChange} sx={fieldSx} />
              <ValidationError prefix="Email" field="email" errors={state.errors} />

              <TextField
                label="Mobile Number"
                name="mobile"
                type="tel"
                fullWidth
                required
                value={fields.mobile}
                onChange={handleChange}
                sx={fieldSx}
                error={mobileError}
                helperText={mobileError ? 'Enter valid 10-digit number (starts 6–9)' : ''}
              />

              {/* State of Registration */}
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>State of Registration</InputLabel>
                <Select
                  name="state"
                  value={fields.state}
                  label="State of Registration"
                  onChange={handleChange}
                  sx={selectSx}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 280, fontFamily: F.body } } }}
                >
                  {INDIAN_STATES.map((s) => (
                    <MenuItem key={s} value={s} sx={{ fontFamily: F.body, fontSize: 14 }}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Type of Registration */}
              <FormControl fullWidth required>
                <InputLabel sx={labelSx}>Type of Registration</InputLabel>
                <Select
                  name="registrationType"
                  value={fields.registrationType}
                  label="Type of Registration"
                  onChange={handleChange}
                  sx={selectSx}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 280, fontFamily: F.body } } }}
                >
                  {REGISTRATION_TYPES.map((t) => (
                    <MenuItem key={t} value={t} sx={{ fontFamily: F.body, fontSize: 14 }}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {formStatus === 'success' && (
                <Box sx={{ backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '8px', p: 1.5 }}>
                  <Typography sx={{ fontFamily: F.body, color: '#065F46', fontSize: 13, fontWeight: 500 }}>
                    ✓ Thanks! We'll get back to you within 24 hours.
                  </Typography>
                </Box>
              )}
              {formStatus === 'error' && (
                <Typography sx={{ fontFamily: F.body, color: '#DC2626', fontSize: 13 }}>
                  Something went wrong. Please try again.
                </Typography>
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={state.submitting}
                endIcon={<ArrowForwardIcon />}
                sx={{ ...btnBlueSx, mt: 0.5, py: 1.5, fontSize: 15, '&:disabled': { opacity: 0.6 } }}
              >
                Book Now
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* ── 3-step process strip ── */}
      <Box
        sx={{
          maxWidth:     1200,
          mx:           'auto',
          mt:           { xs: 5, md: 6 },
          pt:           { xs: 4, md: 5 },
          borderTop:    `1px solid ${C.border}`,
        }}
      >
        <Typography
          sx={{
            fontFamily:    F.heading,
            fontSize:      11,
            fontWeight:    700,
            letterSpacing: 3,
            textTransform: 'uppercase',
            color:         C.textLight,
            textAlign:     'center',
            mb:            3,
          }}
        >
          How it works
        </Typography>

        <Box
          sx={{
            display:        'flex',
            flexDirection:  { xs: 'column', md: 'row' },
            alignItems:     { xs: 'center', md: 'flex-start' },
            justifyContent: 'center',
            gap:            { xs: 2, md: 0 },
          }}
        >
          {[
            {
              step:  '01',
              icon:  <CalendarMonthOutlinedIcon sx={{ fontSize: 26 }} />,
              title: 'Book Your Consultation',
              desc:  'Fill the form and our expert will call you within 24 hours.',
              color: C.blue,
            },
            {
              step:  '02',
              icon:  <FolderOpenOutlinedIcon sx={{ fontSize: 26 }} />,
              title: 'Share Details & Documents',
              desc:  'Provide required documents — we guide you at every step.',
              color: '#7C3AED',
            },
            {
              step:  '03',
              icon:  <VerifiedOutlinedIcon sx={{ fontSize: 26 }} />,
              title: 'Pay & Get Registered',
              desc:  'Pay the fees and we handle everything till your company is live.',
              color: C.green,
            },
          ].map((item, idx, arr) => (
            <React.Fragment key={item.step}>
              {/* Step card */}
              <Box
                sx={{
                  display:        'flex',
                  flexDirection:  'column',
                  alignItems:     'center',
                  textAlign:      'center',
                  flex:           1,
                  px:             { xs: 2, md: 3 },
                  maxWidth:       { xs: 320, md: 'none' },
                }}
              >
                <Box
                  sx={{
                    width:           52,
                    height:          52,
                    borderRadius:    '14px',
                    backgroundColor: `${item.color}12`,
                    border:          `1.5px solid ${item.color}30`,
                    display:         'flex',
                    alignItems:      'center',
                    justifyContent:  'center',
                    color:           item.color,
                    mb:              1.5,
                  }}
                >
                  {item.icon}
                </Box>

                <Typography
                  sx={{
                    fontFamily:    F.heading,
                    fontSize:      10,
                    fontWeight:    700,
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    color:         item.color,
                    mb:            0.5,
                  }}
                >
                  Step {item.step}
                </Typography>

                <Typography sx={{ fontFamily: F.heading, fontWeight: 700, fontSize: { xs: 15, md: 16 }, color: C.navy, mb: 0.75, lineHeight: 1.25 }}>
                  {item.title}
                </Typography>

                <Typography sx={{ fontFamily: F.body, fontSize: 13.5, color: C.textMid, lineHeight: 1.65 }}>
                  {item.desc}
                </Typography>
              </Box>

              {/* Connector arrow between steps — desktop only */}
              {idx < arr.length - 1 && (
                <Box
                  sx={{
                    display:        { xs: 'none', md: 'flex' },
                    alignItems:     'center',
                    justifyContent: 'center',
                    color:          C.border,
                    flexShrink:     0,
                    mt:             1.5,
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 22, color: C.textLight }} />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default FormSection;
